/* eslint-disable */
import { ReactComponent as PlayButtonSVG } from '@/assets/images/svg/glyphs/audio/audio-play.svg';
import { ReactComponent as ClickSVG } from '@/assets/images/svg/glyphs/click.svg';
import Heading from '@/components/_atoms/Heading';
import Header from '@/components/_rebrand/Header';
import { PrimaryDefaultButton } from '@/components/buttons/primary';
import ProductTourModal from '@/components/modals/ProductTourModal';
import YouTubeVideoModal from '@/components/modals/YouTubeVideoModal';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import ProductTourFooter from '@/components/pages/product-tour/ProductTourFooter';
import HEADER_PROPS from '@/constants/header';
import { HubspotCompanySizeOptions, HubspotFieldNames } from '@/constants/hubspot';
import { DEMO_REQUESTED_SESSION_KEY } from '@/constants/session-storage';
import { UserContext } from '@/context/user';
import { ProductTourPageQuery } from '@/graphql';
import ProductTourPageQueryDocument from '@/graphql/queries/ProductTourPage.gql';
import useProductTourDecision, { PRODUCT_TOUR_MIN_WIDTH } from '@/lib/hooks/useProductTourDecision';
import { getStaticPageProps } from '@/lib/next';
import SimpleHeroModule from '@/modules/SimpleHero';
import { COLORS, GRADIENTS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { BREAKPOINT_PADDING_VAR, marginX, mediaBreakpointUp } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import Image from 'next/image';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSessionstorageState } from 'rooks';
import styled from 'styled-components';

export interface ProductTourPageProps extends ProductTourPageQuery { }

const ProductTourPage: PageTemplate<ProductTourPageProps> = ({ entry }) => {
  const [showTour, setShowTour] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const isTourEligible = useProductTourDecision();

  const { userData } = useContext(UserContext);
  const [didRequestDemo] = useSessionstorageState<boolean>(DEMO_REQUESTED_SESSION_KEY, false);

  const navatticNoCtas =
    didRequestDemo &&
    !!userData[HubspotFieldNames.CompanySize] &&
    userData[HubspotFieldNames.CompanySize] !== HubspotCompanySizeOptions.XSmall;

  const onTourClick = () => {
    setShowTour(true);
  };

  const onVideoClick = () => {
    setShowVideo(true);
  };

  useEffect(() => {
    const handler = () => {
      if (!videoRef.current || !heroRef.current) return;

      const videoHeight = videoRef.current.clientHeight;
      const parentElement = videoRef.current.parentElement!;

      const padding = videoHeight / 2 + (videoRef.current.offsetTop - parentElement.offsetTop);

      heroRef.current.style.setProperty('--hero-addl-padding', `${padding}px`);
    };

    window.addEventListener('resize', handler);

    handler();

    return () => {
      window.removeEventListener('reisze', handler);
    };
  }, [videoRef.current, heroRef.current]);

  return (
    <PageWrapper>
      <div ref={heroRef}>
        <Hero props={entry?.hero} />

        <Grid>
          <ProductTourWrapper>
            <Heading level={3}>Learn by doing</Heading>

            <VideoWrapper onClick={onTourClick}>
              <Image src="https://front.com/assets/pages/global-ai/Product-Tour/Navattic-Preview.png" width={640} height={360} alt="Interactive tour preview" quality={100} className="w-full rounded-[16px]" />
              <VideoOverlay>
                <PrimaryDefaultButton onClick={onTourClick}>
                  <ClickSVG width={26} /> Take a tour
                </PrimaryDefaultButton>
              </VideoOverlay>
            </VideoWrapper>

            <p>
              Take Front for a spin with an interactive product tour to see how Front streamlines communication for
              exceptional service at scale.
            </p>
          </ProductTourWrapper>

          <DemoWrapper>
            <Heading level={3}>Learn by watching</Heading>

            <VideoWrapper ref={videoRef} onClick={onVideoClick}>
              <Image src="https://front.com/assets/pages/global-ai/Product-Tour/Youtube-Demo-Preview.png" width={640} height={360} alt="Product tour video preview" quality={100} className="w-full rounded-[16px]" />
              <VideoOverlay>
                <PrimaryDefaultButton onClick={onVideoClick}>
                  <PlayButtonSVG width={14} /> Watch video
                </PrimaryDefaultButton>
              </VideoOverlay>
            </VideoWrapper>

            <p>
              Let Jacob, a Front expert, show you around personally to learn how Front powers exceptional customer
              service across every channel.
            </p>
          </DemoWrapper>
        </Grid>
      </div>

      {isTourEligible && showTour && (
        <ProductTourModal
          version={navatticNoCtas ? 'no-ctas' : 'no-demo'}
          onClose={() => {
            setShowTour(false);
          }}
        />
      )}

      {showVideo && (
        <YouTubeVideoModal
          onClose={() => setShowVideo(false)}
          videoId="UKblwIGte34"
          showPlayButton={false}
          videoName="Product Demo"
        />
      )}

      <ProductTourFooter props={entry?.footer} />
    </PageWrapper>
  );
};

export default ProductTourPage;

ProductTourPage.template = (props) => <MasterTemplate navigation={<Header {...HEADER_PROPS} invert />} {...props} />;

export const getStaticProps = getStaticPageProps('product-tour', async ({ client }) => {
  const props = await client.request<ProductTourPageQuery>(ProductTourPageQueryDocument);

  return {
    props,
  };
});

const PageWrapper = styled.div`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

const Hero = styled(SimpleHeroModule)`
  padding-bottom: calc(var(--padding-y) + var(--hero-addl-padding, 300px));

  ${mediaBreakpointUp('desktop')} {
    padding-bottom: calc(var(--padding-y) + var(--hero-addl-padding, 300px));
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding-bottom: calc(var(--padding-y) + var(--hero-addl-padding, 300px));
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }

  video {
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: ${remCalc(0, 0, 22)} ${hexToRGBA(COLORS.BLACK, 0.3)};
    transition: all 0.2s ease-in-out;
  }
`;

const VideoOverlay = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &::before {
    content: '';
    background: ${GRADIENTS.DARK};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.3;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover {
    &::before {
      opacity: 0.15;
    }
  }

  ${PrimaryDefaultButton} {
    position: relative;
    --button-color: ${COLORS.WHITE};
    --button-color-hover: ${COLORS.WHITE};
    --button-text-color: ${COLORS.BLACK};
    --button-text-color-hover: ${COLORS.BLACK};

    display: inline-flex;
    align-items: center;
    gap: 0.5rem;

    box-shadow: 0px 0px 20px 0 ${hexToRGBA(COLORS.BLACK, 0.3)};

    &:hover {
      box-shadow: 0px 0px 20px 0 ${hexToRGBA(COLORS.BLACK, 0.5)};
    }

    svg {
      margin: -20px 0;
    }
  }
`;

const Grid = styled.div`
  width: calc(100% - (${BREAKPOINT_PADDING_VAR} * 2));
  max-width: ${remCalc(1400)};
  ${marginX('auto')};
  margin-top: calc(var(--hero-addl-padding, 300px) * -1);

  @media (min-width: ${remCalc(PRODUCT_TOUR_MIN_WIDTH)}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
  }

  &:hover {
    video {
      filter: grayscale(1);
    }
  }
`;

const DemoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  &:hover {
    video {
      filter: grayscale(0);
    }
  }

  h3 {
    display: none;
    text-align: center;
    margin-bottom: 1.5rem;
    color: ${COLORS.WHITE};
  }

  p {
    max-width: 90%;
    text-align: center;
    margin: 1.5rem auto 0;
  }

  @media (min-width: ${remCalc(PRODUCT_TOUR_MIN_WIDTH)}) {
    h3 {
      display: block;
    }
  }
`;

const ProductTourWrapper = styled(DemoWrapper)`
  display: none;

  @media (min-width: ${remCalc(PRODUCT_TOUR_MIN_WIDTH)}) {
    display: block;
  }
`;
